import React, { useState, useEffect, useRef, Suspense } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import {
  Sheet,
  SheetContent,
  SheetTitle,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import BottomNav from './BottomNav';
import { useAuthStore } from '@/store/auth';
import * as api from '@/api';
import { toast } from 'sonner';
import {
  Menu,
  PanelLeftClose,
  PanelLeftOpen,
  LogOut,
  LayoutDashboard,
  ClipboardList,
  Users,
  UserCog,
  UserCheck,
  Store,
  Wallet,
  CreditCard,
  SlidersHorizontal,
  Users2,
  Settings,
  ShoppingBag,
  Ticket,
  Gamepad2,
  Headphones, MessageCircle,
  Trophy,
  Gift,
  MessageSquare,
  ShieldCheck,
  CircleDot,
  Frame,
  ChevronDown,
  LifeBuoy,
  ScrollText,
  Image as ImageIcon,
} from 'lucide-react';

type Icon = React.ComponentType<{ size?: number; className?: string }>;

interface NavItem {
  path: string;
  label: string;
  icon: Icon;
  end?: boolean;
  permission?: string; // 对应的权限key，如 'orders.view'
}

interface NavGroup {
  title: string;
  items: NavItem[];
}

// 管理员菜单（初步分组，可在侧边栏折叠/展开；细节分类可后续调整）
const adminNavGroups: NavGroup[] = [
  {
    title: '总览',
    items: [
      { path: '/dashboard', label: '仪表盘', icon: LayoutDashboard, end: true },
    ],
  },
  {
    title: '订单与人员',
    items: [
      { path: '/orders', label: '订单管理', icon: ClipboardList, permission: 'orders.view' },
      { path: '/app/player-applications', label: '打手申请审核', icon: UserCheck },
      { path: '/app/after-sales', label: '售后工单', icon: LifeBuoy, permission: 'aftersales.view' },
      { path: '/bosses', label: '老板管理', icon: UserCog, permission: 'bosses.view' },
      { path: '/players', label: '陪玩管理', icon: Users, permission: 'players.view' },
      { path: '/customer-service', label: '客服管理', icon: Headphones },
    ],
  },
  {
    title: '商品与内容',
    items: [
      { path: '/game-items', label: '玩法项目', icon: Gamepad2, permission: 'gameItems.view' },
      { path: '/big-categories', label: '大游戏类目', icon: CircleDot },
      { path: '/shop', label: '商品进销存', icon: Store, permission: 'products.view' },
      { path: '/coupons', label: '优惠券', icon: Ticket, permission: 'coupons.view' },
      { path: '/gifts', label: '礼物管理', icon: Gift },
      { path: '/reviews', label: '评价管理', icon: MessageSquare },
      { path: '/avatar-frames', label: '头像框管理', icon: Frame },
      { path: '/app/player-backgrounds', label: '陪玩背景库', icon: ImageIcon },
      { path: '/app/achievements', label: '成就管理', icon: Trophy },
      { path: '/app/boss-rights', label: '老板权益', icon: ScrollText },
    ],
  },
  {
    title: '财务',
    items: [
      { path: '/finance', label: '财务管理', icon: Wallet, permission: 'finance.view' },
      { path: '/withdrawals', label: '提现审核', icon: CreditCard, permission: 'withdrawals.view' },
    ],
  },
  {
    title: '系统',
    items: [
      { path: '/levels', label: '等级管理', icon: SlidersHorizontal },
      { path: '/users', label: '用户管理', icon: Users2 },
      { path: '/app/internal', label: '内部管理', icon: ShieldCheck },
      { path: '/settings', label: '系统设置', icon: Settings },
    ],
  },
];

// 客服菜单
const csNavGroups: NavGroup[] = [
  {
    title: '客服工作',
    items: [
      { path: '/orders', label: '订单审核', icon: ClipboardList, permission: 'orders.view' },
      { path: '/bosses', label: '老板查询', icon: UserCog, permission: 'bosses.view' },
      { path: '/players', label: '陪玩查询', icon: Users, permission: 'players.view' },
      { path: '/app/after-sales', label: '售后工单', icon: LifeBuoy, permission: 'aftersales.view' },
      { path: '/gifts', label: '礼物登记', icon: Gift },
    ],
  },
];

// 陪玩菜单
const playerNavGroups: NavGroup[] = [
  {
    title: '我的',
    items: [
      { path: '/app/players/account', label: '我的账户', icon: Wallet },
      { path: '/app/my-orders', label: '我的订单', icon: ClipboardList },
      { path: '/app/players/fund-flow', label: '我的资金流水', icon: CreditCard },
      { path: '/app/my-withdrawals', label: '我的兑换', icon: ShoppingBag },
      { path: '/app/players/ranking', label: '营业额排行', icon: Trophy },
    ],
  },
];

const csBottomItems: NavItem[] = [
  { path: '/orders', label: '订单', icon: ClipboardList, permission: 'orders.view' },
  { path: '/bosses', label: '老板', icon: UserCog, permission: 'bosses.view' },
  { path: '/players', label: '陪玩', icon: Users, permission: 'players.view' },
];

const adminBottomItems: NavItem[] = [
  { path: '/dashboard', label: '首页', icon: LayoutDashboard, end: true },
  { path: '/orders', label: '订单', icon: ClipboardList },
  { path: '/players', label: '陪玩', icon: Users },
  { path: '/finance', label: '财务', icon: Wallet },
  { path: '/shop', label: '商城', icon: Store },
];

const playerBottomItems: NavItem[] = [
  { path: '/app/players/account', label: '我的', icon: Wallet },
  { path: '/app/my-orders', label: '订单', icon: ClipboardList },
  { path: '/app/players/ranking', label: '排行', icon: Trophy },
  { path: '/app/my-withdrawals', label: '提现', icon: ShoppingBag },
];

// 侧边栏内容（提取为模块顶层组件，保证组件类型稳定，
// 避免父组件每次渲染都重新挂载导致滚动位置/分组状态丢失）
const SidebarContent = ({
  appName,
  navGroups,
  collapsed,
  user,
  onNavigate,
  forceExpanded,
  onAvatarUpload,
  onLogout,
}: {
  appName: string;
  navGroups: NavGroup[];
  collapsed: boolean;
  user: any;
  onNavigate?: () => void;
  forceExpanded?: boolean;
  onAvatarUpload: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onLogout: () => void;
}) => {
  const expanded = forceExpanded ? true : !collapsed;
  // 折叠的分组（持久化到 localStorage，切换页面/刷新后保留）
  const storageKey = 'nav-closed-groups';
  const [closedGroups, setClosedGroups] = useState<Record<string, boolean>>(() => {
    try {
      return JSON.parse(localStorage.getItem(storageKey) || '{}');
    } catch {
      return {};
    }
  });
  const toggleGroup = (title: string) =>
    setClosedGroups((prev) => {
      const next = { ...prev, [title]: !prev[title] };
      try {
        localStorage.setItem(storageKey, JSON.stringify(next));
      } catch {}
      return next;
    });
  return (
    <div className="flex h-full flex-col">
      <div
        className={cn(
          'flex h-16 shrink-0 items-center gap-3 px-4',
          !expanded && 'lg:justify-center lg:px-0',
        )}
      >
        <img
          src="/logo.jpg"
          alt="logo"
          className="h-9 w-9 shrink-0 rounded-lg object-cover"
          onError={(e) => {
            (e.currentTarget as HTMLImageElement).style.display = 'none';
          }}
        />
        {expanded && (
          <div className="truncate font-bold text-base leading-tight">
            {appName}
          </div>
        )}
      </div>

      <nav
        className="sidebar-nav flex-1 space-y-3 overflow-y-auto p-3"
        style={{ scrollbarWidth: 'none' }}
      >
        {navGroups.map((group) => {
          const isClosed = expanded && !!closedGroups[group.title];
          return (
            <div key={group.title}>
              {expanded ? (
                <button
                  type="button"
                  onClick={() => toggleGroup(group.title)}
                  className="flex w-full items-center justify-between rounded-md px-2 pb-1 text-xs font-medium uppercase tracking-wider text-sidebar-foreground/50 hover:text-sidebar-foreground/80"
                >
                  <span>{group.title}</span>
                  <ChevronDown
                    size={13}
                    className={cn(
                      'shrink-0 transition-transform duration-200',
                      isClosed && '-rotate-90',
                    )}
                  />
                </button>
              ) : null}
              {!isClosed && (
                <div className="space-y-1">
                  {group.items.map((item) => {
                    const Icon = item.icon;
                    return (
                      <NavLink
                        key={item.path}
                        to={item.path}
                        end={item.end}
                        onClick={onNavigate}
                        title={expanded ? undefined : item.label}
                        className={({ isActive }) =>
                          cn(
                            'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-all duration-150',
                            !expanded && 'lg:justify-center lg:px-0',
                            isActive
                              ? 'bg-sidebar-primary text-sidebar-primary-foreground shadow-md'
                              : 'text-sidebar-foreground hover:bg-sidebar-accent hover:shadow-md hover:-translate-y-0.5 hover:scale-[1.02]',
                          )
                        }
                      >
                        <Icon size={18} className="shrink-0" />
                        {expanded && <span className="truncate">{item.label}</span>}
                      </NavLink>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </nav>

      <div className="shrink-0 p-3">
        <div
          className={cn(
            'mb-2 flex items-center gap-2 px-1',
            !expanded && 'lg:justify-center',
          )}
        >
          <label className="relative flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center overflow-hidden rounded-full bg-sidebar-primary/10 text-sm font-semibold text-sidebar-primary" title="点击上传头像">
            {user?.avatarUrl ? (
              <img src={user.avatarUrl} alt="头像" className="h-full w-full object-cover" />
            ) : (
              (user?.username || 'A').slice(0, 1).toUpperCase()
            )}
            <input type="file" accept="image/*" className="hidden" onChange={onAvatarUpload} />
          </label>
          {expanded && (
            <div className="truncate text-sm font-medium">
              {user?.name || user?.username || '用户'}
            </div>
          )}
        </div>
        <Button
          variant="secondary"
          className={cn('w-full', !expanded && 'lg:px-0')}
          onClick={onLogout}
        >
          <LogOut size={16} className={cn(!expanded && 'lg:mr-0', expanded && 'mr-2')} />
          {expanded && '退出登录'}
        </Button>
      </div>
    </div>
  );
};

const Layout = () => {
  const navigate = useNavigate();
  // 全局轮询新待接单
  const lastPendingCount = useRef(-1);
  useEffect(() => {
    // 初始化 AudioContext（用户首次交互时激活）
    let audioCtx: AudioContext | null = null;
    const initAudio = () => {
      if (!audioCtx) {
        audioCtx = new (window.AudioContext || (window as any).webkitAudioContext)();
      }
      if (audioCtx.state === 'suspended') audioCtx.resume();
    };
    window.addEventListener('click', initAudio);
    window.addEventListener('keydown', initAudio);

    const playAlarm = () => {
      try {
        if (!audioCtx) return;
        for (let i = 0; i < 5; i++) {
          const o = audioCtx.createOscillator();
          const g = audioCtx.createGain();
          o.connect(g); g.connect(audioCtx.destination);
          o.frequency.value = i % 2 === 0 ? 880 : 660;
          o.type = 'square';
          const t = audioCtx.currentTime + i * 0.25;
          g.gain.setValueAtTime(0.6, t);
          g.gain.exponentialRampToValueAtTime(0.001, t + 0.25);
          o.start(t);
          o.stop(t + 0.25);
        }
      } catch {}
    };

    const checkPending = async () => {
      try {
        const res = await api.getOrders({ status: '待接单', pageSize: 1 });
        const count = (res as any).total || 0;
        if (lastPendingCount.current >= 0 && count > lastPendingCount.current) {
          toast.success(`🔔 新订单来了！当前待接单 ${count} 单（点击查看）`, {
            duration: 15000,
            style: { fontSize: '18px', padding: '16px 24px' },
            onClick: () => {
              navigate('/orders');
              toast.dismiss();
              // 触发事件，让订单页面切换到待接单标签
              setTimeout(() => {
                window.dispatchEvent(new CustomEvent('switch-to-pending'));
              }, 100);
            }
          });
          window.dispatchEvent(new CustomEvent('new-pending-order'));
          playAlarm();
        }
        lastPendingCount.current = count;
      } catch {}
    };
    checkPending();
    const timer = setInterval(checkPending, 10000);
    return () => {
      clearInterval(timer);
      window.removeEventListener('click', initAudio);
      window.removeEventListener('keydown', initAudio);
    };
  }, []);
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const user = useAuthStore((s) => s.user);

  const isPlayer = user?.role === 'player';
  const isCS = user?.role === 'customer_service' || user?.role === 'support' || user?.role === 'staff';
  let navGroups = adminNavGroups;
  let bottomItems = adminBottomItems;
  let appName = '掌游娱乐管理系统';
  if (isPlayer) {
    navGroups = playerNavGroups;
    bottomItems = playerBottomItems;
    appName = '掌游娱乐陪玩端';
  } else if (isCS) {
    navGroups = csNavGroups;
    bottomItems = csBottomItems;
    appName = '掌游娱乐客服端';
  }

  // 根据权限过滤菜单项（admin默认拥有所有权限，staff根据设置的权限过滤）
  const userPermissions = user?.permissions || {};
  const isAdmin = user?.role === 'admin';
  const hasPermission = (permission?: string) => {
    if (!permission) return true; // 没有指定权限的菜单项默认显示
    if (isAdmin) return true; // admin默认拥有所有权限
    return !!userPermissions[permission];
  };
  const filterNavGroups = (groups: NavGroup[]) => {
    return groups.map(group => ({
      ...group,
      items: group.items.filter(item => hasPermission(item.permission))
    })).filter(group => group.items.length > 0);
  };
  navGroups = filterNavGroups(navGroups);
  bottomItems = bottomItems.filter(item => hasPermission(item.permission));

  const handleLogout = () => {
    useAuthStore.getState().logout();
    navigate('/login');
  };

  const handleSidebarAvatarUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const res = await api.uploadAvatar(file);
      const url = res?.url;
      const cur = useAuthStore.getState().user;
      if (url && cur) {
        useAuthStore.getState().setUser({ ...cur, avatarUrl: url });
        toast.success('头像已更新');
      }
    } catch {
      toast.error('头像上传失败');
    }
  };

  return (
    <div className="flex h-screen overflow-hidden bg-background text-foreground">
      {/* 桌面端侧边栏 */}
      <aside
        className={cn(
          'hidden shrink-0 flex-col border-r border-sidebar-border bg-sidebar transition-[width] duration-200 lg:flex',
          collapsed ? 'w-[72px]' : 'w-64',
        )}
      >
        <SidebarContent
          appName={appName}
          navGroups={navGroups}
          collapsed={collapsed}
          user={user}
          onAvatarUpload={handleSidebarAvatarUpload}
          onLogout={handleLogout}
        />
      </aside>

      {/* 移动端抽屉 */}
      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent side="left" className="w-72 max-w-[85vw] p-0">
          <SheetTitle className="sr-only">导航菜单</SheetTitle>
          <SidebarContent
            appName={appName}
            navGroups={navGroups}
            collapsed={collapsed}
            user={user}
            onNavigate={() => setMobileOpen(false)}
            forceExpanded
            onAvatarUpload={handleSidebarAvatarUpload}
            onLogout={handleLogout}
          />
        </SheetContent>
      </Sheet>

      {/* 主区域 */}
      <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
        <header className="flex h-14 shrink-0 items-center gap-2 bg-background px-3 md:px-4">
          <Button
            variant="ghost"
            size="icon"
            className="lg:hidden"
            onClick={() => setMobileOpen(true)}
            aria-label="打开菜单"
          >
            <Menu size={20} />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="hidden lg:inline-flex"
            onClick={() => setCollapsed((c) => !c)}
            aria-label={collapsed ? '展开侧边栏' : '折叠侧边栏'}
          >
            {collapsed ? <PanelLeftOpen size={20} /> : <PanelLeftClose size={20} />}
          </Button>
          <div className="font-semibold lg:hidden">{appName}</div>

          <div className="ml-auto flex items-center gap-2">
            <span className="hidden text-sm text-muted-foreground sm:inline">
              {user?.username || '用户'}
            </span>
            <Button
              variant="ghost"
              size="icon"
              onClick={handleLogout}
              aria-label="退出登录"
            >
              <LogOut size={18} />
            </Button>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto p-4 pb-20 md:p-6 lg:pb-6">
          <Suspense fallback={<div className="min-h-[60vh]" />}>
            <Outlet />
          </Suspense>
          <footer className="mt-8 pb-4 text-center text-xs text-slate-400">
            <a
              href="https://beian.miit.gov.cn"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-slate-500"
            >
              赣ICP备2026025821号
            </a>
          </footer>
        </main>

        <BottomNav items={bottomItems} />
      </div>
    </div>
  );
};

export default Layout;
